import React, { useContext, useState, useEffect, useRef } from 'react'
import './NewCollections.css'
import Item from '../Item/Item'
import { ShopContext } from '../../Context/ShopContext'

const NewCollections = () => {
    const { all_product, loading, error } = useContext(ShopContext);
    const [showAll, setShowAll] = useState(false);
    const [isHighlighted, setIsHighlighted] = useState(false);
    const itemsToShow = 16; // Show 16 items initially (4 columns x 4 rows)
    const sectionRef = useRef(null);
    
    // Check if this section should be highlighted (when navigated via hash)
    useEffect(() => {
        // Check URL hash on component mount
        if (window.location.hash === '#new-arrivals-section') {
            setIsHighlighted(true);
            
            // Remove the hash from URL after scrolling
            setTimeout(() => {
                window.history.replaceState(null, null, ' ');
            }, 1000);
            
            // Remove highlight after 2 seconds
            setTimeout(() => {
                setIsHighlighted(false);
            }, 2000);
        }
    }, []);
    
    // Listen for hash changes
    useEffect(() => {
        const handleHashChange = () => {
            if (window.location.hash === '#new-arrivals-section') {
                setIsHighlighted(true);
                
                setTimeout(() => {
                    window.history.replaceState(null, null, ' ');
                }, 1000);
                
                setTimeout(() => {
                    setIsHighlighted(false);
                }, 2000);
            }
        };
        
        window.addEventListener('hashchange', handleHashChange);
        return () => window.removeEventListener('hashchange', handleHashChange);
    }, []);
    
    // Show loading state
    if (loading) {
        return (
            <div className='new-collections' id="new-arrivals-section" ref={sectionRef}>
                <h1>NEW ARRIVALS</h1>  
            
                <div className="loading-state">
                    <div className="loading-spinner"></div>
                    <p>Loading latest products...</p>
                </div>
            </div>
        );
    }
    
    // Show error state
    if (error) {
        return (
            <div className='new-collections' id="new-arrivals-section" ref={sectionRef}>
                <h1>NEW ARRIVALS</h1>  
                
                <div className="error-state">
                    <h3>⚠️ Connection Issue</h3>
                    <p>{error}</p>
                    <div className="error-suggestions">
                        <p><strong>Please make sure:</strong></p>
                        <ul>
                            <li>Backend server is running on port 4000</li>
                            <li>Database connection is working</li>
                            <li>No firewall blocking the connection</li>
                        </ul>
                    </div>
                </div>
            </div>
        );
    }
    
    // Check if all_product is valid array
    if (!all_product || !Array.isArray(all_product)) {
        console.error("all_product is not an array:", all_product);
        return (
            <div className='new-collections' id="new-arrivals-section" ref={sectionRef}>
                <h1>NEW ARRIVALS</h1>  
               
                <div className="data-error-state">
                    <p>No products data available.</p>
                    <p>Please check if products are loaded correctly.</p>
                    <p className="data-type">Data type received: {typeof all_product}</p>
                </div>
            </div>
        );
    }
    
    // Show empty state
    if (all_product.length === 0) {
        return (
            <div className='new-collections' id="new-arrivals-section" ref={sectionRef}>
                <h1>NEW ARRIVALS</h1>  
               
                <div className="empty-state">
                    <div className="empty-icon">📦</div>
                    <p>No products found in database.</p>
                    <p className="empty-subtext">Check back later for new arrivals!</p>
                </div>
            </div>
        );
    }
    
    // Sort products by date (newest first) - assuming there's a date field
    // If not, we'll just use the last items in the array
    const sortedProducts = [...all_product].sort((a, b) => {
        // Try to sort by date if available
        if (a.createdAt && b.createdAt) {
            return new Date(b.createdAt) - new Date(a.createdAt);
        }
        if (a.date_added && b.date_added) {
            return new Date(b.date_added) - new Date(a.date_added);
        }
        // Fallback to reverse order (assuming newer items are at the end)
        return 0;
    });
    
    // Get the newest products
    const all_collection = sortedProducts.slice(0, Math.min(sortedProducts.length, 32)); // Limit to 32 items max
    const new_collection = showAll ? all_collection : all_collection.slice(0, itemsToShow);
    const hasMoreItems = all_collection.length > itemsToShow;
    
    // Get count of new arrivals (last 30 days) - if date field exists
    const getNewArrivalsCount = () => {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        
        return all_product.filter(product => {
            const productDate = product.createdAt || product.date_added;
            if (!productDate) return false;
            return new Date(productDate) > thirtyDaysAgo;
        }).length;
    };
    
    const newArrivalsCount = getNewArrivalsCount();

    return (
        <div 
            className={`new-collections ${isHighlighted ? 'highlighted' : ''}`} 
            id="new-arrivals-section" 
            ref={sectionRef}
        >
            <div className="section-header">
                <h1>NEW ARRIVALS</h1>
                {newArrivalsCount > 0 && (
                    <span className="new-count-badge">
                        {newArrivalsCount} NEW
                    </span>
                )}
            </div>
            <hr/>
            <div className="section-subtitle">
                <p>Discover our latest fashion collections and trending styles</p>
            </div>
            
            <div className="collections">
                {new_collection.map((item, i) => {
                    // Handle different image field names
                    const imageUrl = item.image || item.main_image || 
                                   (item.images && item.images[0]) || 
                                   '/images/placeholder.jpg';
                    
                    // Check if item is actually new (within 7 days)
                    const isNewItem = () => {
                        const productDate = item.createdAt || item.date_added;
                        if (!productDate) return false;
                        const sevenDaysAgo = new Date();
                        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
                        return new Date(productDate) > sevenDaysAgo;
                    };
                    
                    return (
                        <Item  
                            key={item.id || i} 
                            id={item.id} 
                            name={item.name} 
                            image={imageUrl} 
                            new_price={item.new_price} 
                            old_price={item.old_price}
                            description={item.description}
                            category={item.category}
                            isNew={isNewItem()}
                        />
                    );
                })}
            </div>
            
            {hasMoreItems && (
                <div className="view-more-container">
                    <button 
                        className={`view-more-btn ${showAll ? 'show-less' : ''}`} 
                        onClick={() => setShowAll(!showAll)}
                    >
                        {showAll ? (
                            <>
                                <span>Show Less</span>
                                <span className="btn-icon">↑</span>
                            </>
                        ) : (
                            <>
                                <span>View All New Arrivals</span>
                                <span className="btn-icon">↓</span>
                                <span className="item-count">({all_collection.length} items)</span>
                            </>
                        )}
                    </button>
                </div>
            )}
            
            {/* Scroll indicator (only shows when section is highlighted) */}
            {isHighlighted && (
                <div className="scroll-indicator">
                    <div className="indicator-dot"></div>
                    <div className="indicator-text">You're viewing New Arrivals</div>
                </div>
            )}
        </div>
    );
}

export default NewCollections;
